import React, { useEffect, useMemo, useState } from 'react';
import { Modal, message, Spin, Divider } from 'antd';
import {
  ctaCreateTicket,
  checkPartnerTicketLimit,
} from '../../services/ctaService.js';
import { getPartner, getSocialMediaPlatforms, getPartnerBrands } from '../../services/conversationService.js';
import CtaBrandRow from './CtaBrandRow.jsx';
import CtaInfoSection from './CtaInfoSection.jsx';
import { useGlobalState } from '../../hooks/useGlobalState.js';

const CtaTicketCreateModal = ({
  visible,
  onCancel,
  onSuccess,
  eligibleRecords = [],
}) => {
  const { userProfile } = useGlobalState();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [brands, setBrands] = useState([]);
  const [platformOptions, setPlatformOptions] = useState([]);
  const [ticketDetails, setTicketDetails] = useState({});

  useEffect(() => {
    if (visible && eligibleRecords.length > 0) {
      loadDependencies();
      initTicketDetails();
    }
  }, [visible]);

  const loadDependencies = async () => {
    setLoading(true);
    try {
      const companyId = userProfile?.user?.companyId;

      const partnerRes = await getPartner(companyId);
      const partner = partnerRes?.result ?? null;
      setPartnerInfo(partner);

      const platformRes = await getSocialMediaPlatforms();
      const sorted = [...(platformRes?.result?.data ?? [])].sort((a, b) => {
        if (a.type === 'CHINESE' && b.type === 'FOREIGN') return -1;
        if (a.type === 'FOREIGN' && b.type === 'CHINESE') return 1;
        return 0;
      });
      setPlatformOptions(
        sorted.map((p) => ({
          value: p.platformId,
          label: p.englishName || p.chineseName || p.platformId,
        }))
      );

      if (partner?.partnerId) {
        const brandsRes = await getPartnerBrands(partner.partnerId);
        const brandsMap = (brandsRes ?? []).map((brand) => ({
          value: brand.brandId,
          label: brand.englishName || brand.chineseName || brand.brandId,
        }));
        setBrands(brandsMap);
      } else {
        setBrands([]);
      }
    } catch (error) {
      console.error('Fail to load dependencies:', error);
      message.error('Failed to load required data.');
    } finally {
      setLoading(false);
    }
  };

  const initTicketDetails = () => {
    const details = {};
    eligibleRecords.forEach((record) => {
      details[record.ctaId] = {
        ctaId: record.ctaId,
        cta: record.cta,
        type: record.ctaType,
        brand: '',
        platform: '',
      };
    });
    setTicketDetails(details);
  };

  const handleBrandChange = (ctaId, value) => {
    setTicketDetails((prev) => ({
      ...prev,
      [ctaId]: { ...prev[ctaId], brand: value },
    }));
  };

  const handlePlatformChange = (ctaId, value) => {
    setTicketDetails((prev) => ({
      ...prev,
      [ctaId]: { ...prev[ctaId], platform: value },
    }));
  };

  const handleTicketLimitError = (response) => {
    const limitData = response?.data || {};
    const limitType = String(limitData?.type || '').toLowerCase();
    const limitCheckFail = limitData?.limitCheckFail === true;

    if (!limitCheckFail) {
      message.error(limitData?.errorMsg || response?.msg || 'Quota check failed.');
      return;
    }

    if (limitType === 'expiration') {
      Modal.error({
        title: 'Expiration Time Reached',
        content:
          'Your partnership service has expired. Please renew your service or contact info@cyberoo.ai for assistance.',
      });
    } else if (limitType === 'limit') {
      Modal.error({
        title: 'Partner Quota Limit',
        content:
          "Your partner's ticket quota has been fully used. Please upgrade your plan or contact info@cyberoo.ai for more capacity.",
      });
    } else {
      message.error(limitData?.errorMsg || response?.msg || 'Quota check failed.');
    }
  };

  const handleOk = async () => {
    const partnerId = partnerInfo?.partnerId;
    const partnerName = partnerInfo?.partnerName || '';
    const companyId = userProfile?.user?.companyId;
    const companyName = userProfile?.user?.companyName || '';

    if (!partnerId) {
      message.error('Partner information is missing.');
      return;
    }

    const ticketItems = Object.values(ticketDetails);

    if (ticketItems.some((item) => !item.brand)) {
      message.warning(
        'Please select a brand for each CTA before creating tickets.'
      );
      return;
    }

    const phoneItems = ticketItems.filter((item) => item.type === 'Phone');
    if (phoneItems.some((item) => !item.platform)) {
      message.warning(
        'Please complete the platform selection for each phone CTA before creating tickets.'
      );
      return;
    }

    const ticketLimitGroups = ticketItems.reduce((groups, item) => {
      const key = `${partnerId}-${item.brand}`;
      if (!groups[key]) {
        groups[key] = {
          partnerId,
          brandId: item.brand,
          count: 0,
        };
      }
      groups[key].count += 1;
      return groups;
    }, {});

    setConfirmLoading(true);

    try {
      for (const group of Object.values(ticketLimitGroups)) {
        const response = await checkPartnerTicketLimit({
          partnerId: group.partnerId,
          brandId: group.brandId,
          batchSize: group.count > 1 ? group.count : 1,
        });

        if (response?.code !== 200 || response?.data?.limitCheckFail === true) {
          handleTicketLimitError(response);
          return;
        }
      }

      const phones = [];
      const urls = [];
      const ctaLogs = [];
      const email = userProfile?.user?.userName || userProfile?.user?.nickName || '';

      ticketItems.forEach((item) => {
        const brandLabel =
          brands.find((b) => b.value === item.brand)?.label || item.brand;

        if (item.type === 'Phone') {
          phones.push({
            brand: item.brand,
            brandName: brandLabel,
            platform: item.platform,
            cta: item.cta,
            type: item.type,
            conversationId: '',
            email,
            partnerId,
            partnerName,
            ctaId: item.ctaId,
            companyId,
            companyName,
          });
          ctaLogs.push(`CTA: ${item.cta}`);
        } else if (item.type === 'URL') {
          urls.push({
            brand: item.brand,
            brandName: brandLabel,
            cta: item.cta,
            type: item.type,
            conversationId: '',
            email,
            partnerId,
            partnerName,
            ctaId: item.ctaId,
            companyId,
            companyName,
          });
          ctaLogs.push(`CTA: ${item.cta}`);
        }
      });

      const logContent = ctaLogs.join('\n');
      const userId = userProfile?.user?.userId || '';
      const userName =
        userProfile?.user?.nickName ||
        userProfile?.user?.name ||
        userProfile?.user?.userName ||
        '';

      const createTicketResponse = await ctaCreateTicket({
        phones,
        urls,
        logContent,
        userId,
        userName,
      });

      if (
        createTicketResponse?.code === 200 &&
        createTicketResponse?.success === true
      ) {
        message.success('Ticket created successfully.');
        onCancel();
        onSuccess();
      } else {
        message.error(
          createTicketResponse?.msg || 'Ticket creation failed.'
        );
      }
    } catch (error) {
      console.error('Create ticket error:', error);
      message.error('An error occurred while creating tickets.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const renderCtaForm = (record) => {
    const detail = ticketDetails[record.ctaId] || {};
    if (record.ctaType === 'Phone') {
      return (
        <CtaInfoSection
          key={record.ctaId}
          cta={record.cta}
          platforms={platformOptions}
          platformValue={detail.platform}
          brands={brands}
          brandValue={detail.brand}
          onPlatformChange={(value) => handlePlatformChange(record.ctaId, value)}
          onBrandChange={(value) => handleBrandChange(record.ctaId, value)}
        />
      );
    }
    return (
      <CtaBrandRow
        key={record.ctaId}
        cta={record.cta}
        brands={brands}
        brandValue={detail.brand}
        onBrandChange={(value) => handleBrandChange(record.ctaId, value)}
      />
    );
  };

  return (
    <Modal
      title="Create Ticket"
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      width={720}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {eligibleRecords.map((record) => (
          <div key={record.ctaId}>
            {renderCtaForm(record)}
            <Divider style={{ margin: '16px 0' }} />
          </div>
        ))}
      </Spin>
    </Modal>
  );
};

export default CtaTicketCreateModal;
